import {
  FREQUENCY_OPTIONS,
  GOAL_OPTIONS,
  LEVEL_OPTIONS,
  LOADER_MESSAGES,
  t,
  type Locale,
} from "./i18n";

// AI Personal Trainer (Phase 8): 3-question form -> submit to the aipt-worker Function URL ->
// rotating loader -> render the plan (or a localized error) -> thumbs feedback. The generated
// plan itself is a pure live passthrough, nothing is stored. The optional "contact me" form
// below the plan is the one exception: submitting it persists the visitor's email, consent,
// and question-form answers server-side for the gym to follow up on (see aipt-worker).

const FEEDBACK_ENDPOINT = "https://25k9t136h9.execute-api.eu-west-1.amazonaws.com/prod/feedback";
const LOADER_ROTATE_MS = 4000;

interface AiptSuccess {
  status: "ok";
  plan: string;
  presignedUrl: string | null;
  runId: string;
}
interface AiptFailure {
  status: "error";
  code: "access_denied" | "unavailable" | "malformed_upstream_response" | "validation_failed" | string;
}
type AiptResult = AiptSuccess | AiptFailure;

export interface AiptContext {
  slug: string;
  premiseId: number | string;
  locale: Locale;
  aiptEndpoint: string;
  /** Whether the "the gym may contact me" form should render. A premise-level setting
   *  (app_settings.gp_widget.features.aipt_email), not something visitors can turn on. */
  emailEnabled: boolean;
  /** Called once per valid form submission, for usage stats. */
  onSubmit?: () => void;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]!);
}

function renderForm(locale: Locale): string {
  const goalOptions = GOAL_OPTIONS.map(
    (goal) => `
    <label class="ii-gp-aipt__option">
      <input type="checkbox" name="goal" value="${goal}" />
      ${escapeHtml(t(locale, `aiptGoal.${goal}`))}
    </label>`,
  ).join("");

  const levelOptions = LEVEL_OPTIONS.map(
    (level) => `
    <label class="ii-gp-aipt__option">
      <input type="radio" name="level" value="${level}" />
      ${escapeHtml(t(locale, `aiptLevel.${level}`))}
    </label>`,
  ).join("");

  const frequencyOptions = FREQUENCY_OPTIONS.map(
    (freq) => `
    <label class="ii-gp-aipt__option">
      <input type="radio" name="frequency" value="${freq}" />
      ${escapeHtml(t(locale, `aiptFrequency.${freq}`))}
    </label>`,
  ).join("");

  return `
<form class="ii-gp-aipt__form">
  <p class="ii-gp-aipt__description">${escapeHtml(t(locale, "aiptDescription"))}</p>
  <fieldset>
    <legend>${escapeHtml(t(locale, "aiptGoalQuestion"))}</legend>
    ${goalOptions}
  </fieldset>
  <fieldset>
    <legend>${escapeHtml(t(locale, "aiptLevelQuestion"))}</legend>
    ${levelOptions}
  </fieldset>
  <fieldset>
    <legend>${escapeHtml(t(locale, "aiptFrequencyQuestion"))}</legend>
    ${frequencyOptions}
  </fieldset>
  <button type="submit" class="ii-gp-aipt__submit">${escapeHtml(t(locale, "aiptSubmit"))}</button>
  <p class="ii-gp-aipt__disclaimer">${escapeHtml(t(locale, "aiptDisclaimer"))}</p>
</form>`;
}

function renderLoader(locale: Locale): string {
  return `
<div class="ii-gp-aipt__loader">
  <p class="ii-gp-aipt__loader-header">${escapeHtml(t(locale, "aiptLoaderHeader"))}</p>
  <p class="ii-gp-aipt__loader-message"></p>
</div>`;
}

function startLoaderRotation(container: HTMLElement, locale: Locale): () => void {
  const messageEl = container.querySelector<HTMLParagraphElement>(".ii-gp-aipt__loader-message");
  const messages = LOADER_MESSAGES[locale];
  let index = 0;
  const setMessage = () => {
    if (messageEl) messageEl.textContent = messages[index % messages.length];
    index += 1;
  };
  setMessage();
  const intervalId = window.setInterval(setMessage, LOADER_ROTATE_MS);
  return () => window.clearInterval(intervalId);
}

async function postFeedback(payload: Record<string, unknown>): Promise<void> {
  try {
    await fetch(FEEDBACK_ENDPOINT, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    console.error("[ii-gp-widget] AI-PT feedback vote failed to send", error);
  }
}

function renderAnswer(result: AiptResult, context: AiptContext): string {
  const { locale } = context;

  if (result.status === "error") {
    const message =
      result.code === "access_denied"
        ? t(locale, "aiptErrorAccessDenied")
        : result.code === "malformed_upstream_response"
          ? t(locale, "aiptErrorGeneric")
          : t(locale, "aiptErrorUnavailable");
    return `<div class="ii-gp-aipt__error">${escapeHtml(message)}</div>`;
  }

  const planHtml = escapeHtml(result.plan).replace(/\n/g, "<br>");
  const pdfLink = result.presignedUrl
    ? `<a class="ii-gp-aipt__pdf" href="${escapeHtml(result.presignedUrl)}" target="_blank" rel="noopener">${escapeHtml(t(locale, "aiptDownloadPdf"))}</a>`
    : "";
  const emailForm = context.emailEnabled
    ? `
  <form class="ii-gp-aipt__email" novalidate>
    <label class="ii-gp-aipt__email-label" for="ii-gp-aipt-email-${escapeHtml(result.runId)}">${escapeHtml(t(locale, "aiptEmailPrompt"))}</label>
    <div class="ii-gp-aipt__email-row">
      <input id="ii-gp-aipt-email-${escapeHtml(result.runId)}" class="ii-gp-aipt__email-input" type="email" inputmode="email" autocomplete="email" required placeholder="${escapeHtml(t(locale, "aiptEmailPlaceholder"))}" />
      <button type="submit" class="ii-gp-aipt__email-send">${escapeHtml(t(locale, "aiptEmailSend"))}</button>
    </div>
    <label class="ii-gp-aipt__email-consent">
      <input class="ii-gp-aipt__email-consent-box" type="checkbox" required />
      <span>${escapeHtml(t(locale, "aiptEmailConsent"))}</span>
    </label>
    <p class="ii-gp-aipt__email-consent-note">${escapeHtml(t(locale, "aiptEmailConsentNote"))}</p>
    <p class="ii-gp-aipt__email-status" hidden></p>
  </form>`
    : "";

  return `
<div class="ii-gp-aipt__answer">
  <h3 class="ii-gp-aipt__answer-title">${escapeHtml(t(locale, "aiptAnswerTitle"))}</h3>
  <p class="ii-gp-aipt__answer-plan">${planHtml}</p>
  ${pdfLink}
  ${emailForm}
  <div class="ii-gp-aipt__thumbs">
    <button type="button" class="ii-gp-aipt__thumb" data-vote="1">👍 ${escapeHtml(t(locale, "aiptThumbsUp"))}</button>
    <button type="button" class="ii-gp-aipt__thumb" data-vote="0">👎 ${escapeHtml(t(locale, "aiptThumbsDown"))}</button>
  </div>
  <p class="ii-gp-aipt__thanks" hidden>${escapeHtml(t(locale, "aiptFeedbackThanks"))}</p>
</div>`;
}

/**
 * Real bug fixed here, not ported: the old down-vote handler sent the key `scoore` instead
 * of `score`. Pulled out as its own pure function so that fix is locked in by a unit test
 * without needing a DOM or a real network call to the (external, production) feedback
 * endpoint just to prove it.
 */
export function buildThumbsVotePayload(vote: 0 | 1, premiseId: number | string, runId: string): Record<string, unknown> {
  return { score: vote, question_id: 99, premise: String(premiseId), run_id: runId };
}

function wireThumbs(container: HTMLElement, runId: string, context: AiptContext): void {
  const thumbs = container.querySelectorAll<HTMLButtonElement>(".ii-gp-aipt__thumb");
  const thanks = container.querySelector<HTMLParagraphElement>(".ii-gp-aipt__thanks");

  thumbs.forEach((button) => {
    button.addEventListener("click", () => {
      thumbs.forEach((b) => (b.disabled = true));
      void postFeedback(buildThumbsVotePayload(Number(button.dataset.vote) === 1 ? 1 : 0, context.premiseId, runId));
      if (thanks) thanks.hidden = false;
    });
  });
}

/** The 3 answers the visitor gave on the question form — re-sent with the contact request
 *  since nothing from `/submit` is persisted server-side to look them up by afterwards. */
export interface AiptSelections {
  goals: string[];
  level: string;
  frequency: string;
}

/**
 * "The gym may contact me about personal training": posts the visitor's address, consent and
 * question-form answers so the gym can follow up. The button disables while in flight so a
 * double tap can't send twice.
 */
function wireEmail(container: HTMLElement, result: AiptSuccess, context: AiptContext, selections: AiptSelections): void {
  const form = container.querySelector<HTMLFormElement>(".ii-gp-aipt__email");
  const input = container.querySelector<HTMLInputElement>(".ii-gp-aipt__email-input");
  const send = container.querySelector<HTMLButtonElement>(".ii-gp-aipt__email-send");
  const consent = container.querySelector<HTMLInputElement>(".ii-gp-aipt__email-consent-box");
  const status = container.querySelector<HTMLParagraphElement>(".ii-gp-aipt__email-status");
  if (!form || !input || !send || !status) return;

  const showStatus = (text: string, isError: boolean) => {
    status.textContent = text;
    status.classList.toggle("ii-gp-aipt__email-status--error", isError);
    status.hidden = false;
  };

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const email = input.value.trim();
    if (!email || !form.checkValidity()) {
      form.reportValidity();
      return;
    }
    send.disabled = true;
    input.disabled = true;
    if (consent) consent.disabled = true;
    const url = new URL(`aipt/${context.slug}/email`, context.aiptEndpoint);
    fetch(url, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        lang: context.locale,
        email,
        runId: result.runId,
        goals: selections.goals,
        level: selections.level,
        frequency: selections.frequency,
        consent: consent?.checked ?? false,
      }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        form.classList.add("ii-gp-aipt__email--sent");
        showStatus(t(context.locale, "aiptEmailSent"), false);
      })
      .catch((error) => {
        console.error("[ii-gp-widget] AI-PT contact request failed", error);
        send.disabled = false;
        input.disabled = false;
        if (consent) consent.disabled = false;
        showStatus(t(context.locale, "aiptEmailError"), true);
      });
  });
}

export function mountAiptTab(container: HTMLElement, context: AiptContext): void {
  container.innerHTML = renderForm(context.locale);

  const form = container.querySelector<HTMLFormElement>(".ii-gp-aipt__form");
  form?.addEventListener("submit", (event) => {
    event.preventDefault();
    const formData = new FormData(form);
    const goals = formData.getAll("goal").map(String);
    const level = formData.get("level");
    const frequency = formData.get("frequency");

    if (goals.length === 0 || !level || !frequency) return; // no i18n string for this — the disabled-until-valid UX is the guard

    const selections: AiptSelections = { goals, level: String(level), frequency: String(frequency) };
    context.onSubmit?.();

    container.innerHTML = renderLoader(context.locale);
    const stopLoader = startLoaderRotation(container, context.locale);

    const submitUrl = new URL(`aipt/${context.slug}/submit`, context.aiptEndpoint);
    fetch(submitUrl, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ lang: context.locale, ...selections }),
    })
      .then((response) => response.json() as Promise<AiptResult>)
      .then((result) => {
        stopLoader();
        container.innerHTML = renderAnswer(result, context);
        if (result.status === "ok") {
          wireThumbs(container, result.runId, context);
          if (context.emailEnabled) wireEmail(container, result, context, selections);
        }
      })
      .catch((error) => {
        console.error("[ii-gp-widget] AI-PT submission failed", error);
        stopLoader();
        container.innerHTML = renderAnswer({ status: "error", code: "unavailable" }, context);
      });
  });
}
